import { defineStore } from "pinia";
import { ref } from "vue";

export interface SavedAccount {
  userId: string;
  fullName: string;
  email: string;
  avatarUrl?: string | null;
  lastLoginAt: number;
}

const SAVED_ACCOUNTS_KEY = "sso_saved_accounts";

// Jeda maksimal akun boleh tampil di "Choose an account" sejak terakhir dipakai
// (7 hari). Harus sinkron dengan SESSION_TTL_SEC di sso-backend
// (session.service.ts) — keduanya memakai kebijakan sliding yang sama.
const ACCOUNT_IDLE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export const useSavedAccountStore = defineStore("savedAccountStore", () => {
  const accounts = ref<SavedAccount[]>([]);

  function persist(list: SavedAccount[]) {
    accounts.value = list;

    if (list.length === 0) {
      localStorage.removeItem(SAVED_ACCOUNTS_KEY);
    } else {
      localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(list));
    }
  }

  // Load dan validasi data dari localStorage
  function loadAccounts(): SavedAccount[] {
    const rawData = localStorage.getItem(SAVED_ACCOUNTS_KEY);
    if (!rawData) {
      accounts.value = [];
      return [];
    }

    try {
      const parsed = JSON.parse(rawData);

      // Defensive check: pastikan array dan atribut sesuai
      if (Array.isArray(parsed)) {
        const validAccounts = parsed.filter(
          (item): item is SavedAccount => typeof item === "object" && item !== null && typeof item.userId === "string" && typeof item.email === "string" && typeof item.fullName === "string",
        );

        accounts.value = validAccounts;
        return validAccounts;
      }
    } catch {
      // Jika JSON terdistorsi/corrupt, reset otomatis
      localStorage.removeItem(SAVED_ACCOUNTS_KEY);
    }

    accounts.value = [];
    return [];
  }

  // Tambah atau perbarui posisi akun ke paling atas (setelah login penuh/OTP)
  function saveAccount(user: { id: string; fullName: string; email: string; avatarUrl?: string | null }) {
    const currentAccounts = loadAccounts();

    // Filter jika akun sudah pernah tersimpan sebelumnya
    const filtered = currentAccounts.filter((acc) => acc.userId !== user.id);

    persist([
      {
        userId: user.id,
        fullName: user.fullName,
        email: user.email,
        avatarUrl: user.avatarUrl ?? null,
        lastLoginAt: Date.now(),
      },
      ...filtered,
    ]);
  }

  // Perbarui "terakhir dipakai" saat pemilihan akun (silent login) berhasil.
  // Inilah perpanjangan 7 hari sliding di sisi daftar akun: setiap kali akun
  // dipilih, jeda 7 hari dimulai dari awal lagi.
  function touchAccount(userId: string) {
    const currentAccounts = loadAccounts();
    const target = currentAccounts.find((acc) => acc.userId === userId);
    if (!target) return;

    persist([
      { ...target, lastLoginAt: Date.now() },
      ...currentAccounts.filter((acc) => acc.userId !== userId),
    ]);
  }

  // Fallback lokal: buang akun yang tidak dipakai lebih dari 7 hari.
  // Pemangkasan utama tetap dari server (pruneToValid) — fallback ini menjaga
  // daftar tetap benar walau pengecekan ke server belum berhasil dilakukan.
  function removeStaleAccounts() {
    const now = Date.now();
    const currentAccounts = loadAccounts();
    const filtered = currentAccounts.filter((acc) => now - acc.lastLoginAt < ACCOUNT_IDLE_TTL_MS);

    if (filtered.length === currentAccounts.length) {
      accounts.value = currentAccounts;
      return;
    }
    persist(filtered);
  }

  // Sinkron dengan server: buang akun yang sesinya sudah tidak valid di Redis
  // (mis. >7 hari tidak dipilih, atau cookie sso_sessions hilang). Akun yang
  // tersisa dijamin bisa silent-login tanpa password.
  function pruneToValid(validUserIds: string[]) {
    const validSet = new Set(validUserIds);
    const currentAccounts = loadAccounts();
    const filtered = currentAccounts.filter((acc) => validSet.has(acc.userId));

    if (filtered.length === currentAccounts.length) {
      accounts.value = currentAccounts;
      return;
    }
    persist(filtered);
  }

  // Hapus 1 akun spesifik dari daftar
  function removeAccount(userId: string) {
    persist(loadAccounts().filter((acc) => acc.userId !== userId));
  }

  // Hapus seluruh akun (Clear all)
  function clearAll() {
    localStorage.removeItem(SAVED_ACCOUNTS_KEY);
    accounts.value = [];
  }

  return {
    accounts,
    loadAccounts,
    saveAccount,
    touchAccount,
    removeStaleAccounts,
    pruneToValid,
    removeAccount,
    clearAll,
  };
});
